<template>
  <div>
    <div v-for="verlinkung in showVerlinkungen" :key="verlinkung.id">
      <div class="w-100 mb-2 d-flex">
        <AppSelect
          style="width: 40%"
          class="mr-3 verlinkung-typ"
          :value="verlinkung.typ"
          :options="allowedTypes"
          label="Typ"
          placeholder="Wähle einen Typ"
          hide-search
          :state="verlinkung.typ !== null ? true : null"
          :disabled="disabled"
          @input="changeTyp($event, verlinkung.id)"
        />
        <AppSelect
          v-if="verlinkung.typ === 'Reise'"
          style="flex: 1; min-width: 0"
          :value="verlinkung.artifactId"
          :options="optionenMitAuswahl(reisekuerzelOptions, verlinkung, 'reisekuerzel')"
          :state="verlinkung.artifactId === null ? false : true"
          :disabled="disabled"
          text-field="reisekuerzel"
          value-field="id"
          label="Reise"
          placeholder="Reisesuche"
          class="verlinkung-artifact"
          @input="changeArtifactReise($event, verlinkung.id)"
        />
        <AppSelect
          v-if="verlinkung.typ === 'Reisetermin'"
          style="flex: 1; min-width: 0"
          :value="verlinkung.artifactId"
          :options="optionenMitAuswahl(reiseterminkuerzelOptions, verlinkung, 'reiseterminkuerzel')"
          :state="verlinkung.artifactId === null ? false : true"
          :disabled="disabled"
          :internal-search="false"
          text-field="reiseterminkuerzel"
          value-field="id"
          label="Reisetermin"
          placeholder="Reiseterminsuche"
          empty-text="Tippe um Reisetermine zu suchen…"
          class="verlinkung-artifact"
          @input="changeArtifactReisetermin($event, verlinkung.id)"
          @search-change="onSearchChangeReisetermin"
        />
        <b-form-input
          style="flex: 1; min-width: 0"
          v-if="verlinkung.typ === 'Flugverfügbarkeit' || verlinkung.typ === 'Vorgang'"
          @input="changeArtifactFlugverfuegbarkeitOrVorgang($event, verlinkung.typ, verlinkung.id)"
          v-model="verlinkung.artifactId"
          type="number"
          :disabled="disabled"
          :state="getVorgangInputState(verlinkung)"
          placeholder="Gib eine Id ein"
        ></b-form-input>
        <b-form-input
          style="flex: 1; min-width: 0"
          v-if="verlinkung.typ === 'Leistung'"
          :value="verlinkung.artifactText"
          :disabled="disabled"
          :state="verlinkung.artifactId > 0"
        ></b-form-input>
        <div style="margin-left: 8px; width: 14px; display: flex; align-items: center; flex-shrink: 0">
          <i
            v-if="!disabled"
            class="fas fa-times text-danger"
            @click="removeVerlinkung(verlinkung.id)"
            style="cursor: pointer"
          ></i>
        </div>
      </div>

      <div
        v-if="banner && verlinkung.typ === 'Reisetermin' && verlinkung.artifactId === banner.reiseterminId"
        class="vorgaenge-hinweis mb-2"
      >
        <div class="d-flex align-items-center">
          <span class="vorgaenge-hinweis-icon">
            <Info :size="18" />
          </span>
          <span>
            <strong class="vorgaenge-count">
              {{ zugeordneteVorgaenge.length }}
              {{ zugeordneteVorgaenge.length === 1 ? 'Vorgang' : 'Vorgänge' }}
            </strong>
            {{ zugeordneteVorgaenge.length === 1 ? 'ist' : 'sind' }} diesem Reisetermin zugeordnet.
          </span>
        </div>
        <b-button class="alle-verlinken-btn" @click="alleVorgaengeVerlinken" variant="outline-primary">
          Alle verlinken
        </b-button>
      </div>
    </div>

    <b-button v-if="!hideAddButton" class="mt-2" size="sm" variant="outline-primary" @click="addRow">
      <i class="fas fa-plus mr-1"></i>
      Hinzufügen
    </b-button>
    <b-button
      class="mt-2 ml-4"
      size="sm"
      @click="showAllVerlinkungen = !showAllVerlinkungen"
      v-if="!showAllVerlinkungen && verlinkungen.length > 5"
    >
      Zeige alle ({{ verlinkungen.length }})
    </b-button>
    <b-button
      class="mt-2 ml-4"
      size="sm"
      @click="showAllVerlinkungen = !showAllVerlinkungen"
      v-else-if="verlinkungen.length > 5"
    >
      Zeige weniger
    </b-button>
  </div>
</template>

<script>
import buildQuery from 'odata-query';
import { format } from 'date-fns';
import apiService from '@/core/common/services/api.service';
import * as odataService from '@/core/common/services/odata.service';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { Info } from 'lucide-vue';

export default {
  name: 'VerlinkungenEditor',
  components: {
    AppSelect,
    Info,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    allowedTypes: {
      type: Array,
      default: () => ['Reisetermin', 'Vorgang', 'Reise'],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideAddButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      reisekuerzelOptions: [],
      reiseterminkuerzelOptions: [],
      timeoutReiseterminkuerzelSearch: null,
      vorgangValidationTimeouts: {},
      vorgangValidationStatuses: {},
      showAllVerlinkungen: true,
      // Vorgang-Ids des zuletzt gewählten Reisetermins
      zugeordneteVorgaenge: [],
      banner: null,
    };
  },
  computed: {
    verlinkungen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    showVerlinkungen() {
      if (this.showAllVerlinkungen) {
        return this.verlinkungen;
      } else {
        return this.verlinkungen.slice(0, 5);
      }
    },
    isValid() {
      return !this.verlinkungen.some(
        verlinkung =>
          verlinkung.artifactId === null ||
          (verlinkung.typ === 'Vorgang' &&
            verlinkung.id in this.vorgangValidationStatuses &&
            this.vorgangValidationStatuses[verlinkung.id] !== 'valid')
      );
    },
  },
  watch: {
    isValid: {
      immediate: true,
      handler(val) {
        this.$emit('valid', val);
      },
    },
  },
  mounted() {
    this.getReisekuerzel();
  },
  beforeDestroy() {
    Object.values(this.vorgangValidationTimeouts).forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
  },
  methods: {
    addRow() {
      const nextId =
        this.verlinkungen.length > 0 ? this.verlinkungen[this.verlinkungen.length - 1].id + 1 : 0;
      this.verlinkungen = [
        ...this.verlinkungen,
        { id: nextId, typ: null, artifactId: null, artifactText: null },
      ];
    },
    removeVerlinkung(id) {
      this.clearVorgangValidationTimeout(id);
      this.$delete(this.vorgangValidationStatuses, id);
      this.hideBannerBeiReiseterminAenderung(id);
      this.verlinkungen = this.verlinkungen.filter(verlinkung => verlinkung.id !== id);
    },
    changeTyp(neuerTyp, verlinkungId) {
      this.clearVorgangValidationTimeout(verlinkungId);
      this.$set(this.vorgangValidationStatuses, verlinkungId, null);
      this.hideBannerBeiReiseterminAenderung(verlinkungId);
      this.verlinkungen = this.verlinkungen.map(verlinkung => {
        if (verlinkung.id === verlinkungId) {
          return { ...verlinkung, typ: neuerTyp, artifactId: null, artifactText: null };
        } else return verlinkung;
      });
    },
    // Schließt den Hinweis-Banner
    hideBannerBeiReiseterminAenderung(verlinkungId) {
      if (!this.banner) return;
      const verlinkung = this.verlinkungen.find(eintrag => eintrag.id === verlinkungId);
      if (
        verlinkung &&
        verlinkung.typ === 'Reisetermin' &&
        verlinkung.artifactId === this.banner.reiseterminId
      ) {
        this.banner = null;
        this.zugeordneteVorgaenge = [];
      }
    },
    // Ergänzt den bereits verlinkten Eintrag
    optionenMitAuswahl(optionen, verlinkung, textFeld) {
      const vorhanden = (optionen ?? []).some(option => option.id === verlinkung.artifactId);
      if (verlinkung.artifactId === null || vorhanden) {
        return optionen ?? [];
      }
      return [...(optionen ?? []), { id: verlinkung.artifactId, [textFeld]: verlinkung.artifactText }];
    },
    onSearchChangeReisetermin($event) {
      if ($event.length > 0) {
        if (this.timeoutReiseterminkuerzelSearch) {
          clearTimeout(this.timeoutReiseterminkuerzelSearch);
        }
        this.timeoutReiseterminkuerzelSearch = setTimeout(() => {
          apiService
            .get(
              `Reisetermin?$filter=startswith(reiseterminkuerzel,'${$event}') and abreisedatum ge ${format(
                new Date(),
                'yyyy-MM-dd'
              )} and isDeleted eq false&$select=reiseterminkuerzel,id, unterlagenversandDatum, abreisedatum, isDeleted`
            )
            .then(res => {
              const reisetermine = [...res.data.result.value] || [];
              this.reiseterminkuerzelOptions = reisetermine.map(reisetermin => {
                return {
                  reiseterminkuerzel: reisetermin.reiseterminkuerzel,
                  id: reisetermin.id,
                  unterlagenversandDatum: reisetermin.unterlagenversandDatum,
                  abreisedatum: reisetermin.abreisedatum,
                };
              });
            });
        }, 500); // delay
      }
    },
    changeArtifactReisetermin(artifactId, verlinkungId) {
      const gewaehlt = this.reiseterminkuerzelOptions.find(option => option.id === artifactId);
      if (!gewaehlt) {
        return;
      }
      const { id, reiseterminkuerzel, unterlagenversandDatum, abreisedatum } = gewaehlt;

      const existingVerlinkung = this.verlinkungen.some(
        verlinkung => verlinkung.artifactId === id && verlinkung.typ === 'Reisetermin'
      );
      if (existingVerlinkung) {
        this.$bvToast.toast('Dieser Reisetermin wurde bereits verlinkt.', {
          title: 'Warnung',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      this.verlinkungen = this.verlinkungen.map(verlinkung => {
        if (verlinkung.id === verlinkungId) {
          return {
            ...verlinkung,
            artifactId: id,
            artifactText: reiseterminkuerzel,
            unterlagenversandDatum: unterlagenversandDatum,
            abreisedatum: abreisedatum,
          };
        } else return verlinkung;
      });

      if (this.timeoutReiseterminkuerzelSearch) {
        clearTimeout(this.timeoutReiseterminkuerzelSearch);
        this.timeoutReiseterminkuerzelSearch = null;
      }
      this.reiseterminkuerzelOptions = [];

      // Aufrufer entscheidet über kontextspezifische Folgeaktionen (z.B. Priorität anpassen)
      this.$emit('reisetermin-selected', { id, reiseterminkuerzel, unterlagenversandDatum, abreisedatum });

      // Prüft im Hintergrund, ob dem Reisetermin Vorgänge zugeordnet sind
      this.ladeZugeordneteVorgaenge(id, reiseterminkuerzel);
    },
    async ladeZugeordneteVorgaenge(reiseterminId, reiseterminkuerzel) {
      this.banner = null;
      this.zugeordneteVorgaenge = [];
      try {
        const response = await odataService.getVorgang({
          filter: {
            hotelleistungen: { any: { isDeleted: false, reiseterminId: parseInt(reiseterminId) } },
          },
          select: ['id'],
        });
        const ids = Array.isArray(response?.data) ? response.data.map(vorgang => vorgang.id) : [];
        if (ids.length > 0) {
          this.zugeordneteVorgaenge = ids;
          this.banner = { reiseterminId, reiseterminkuerzel };
        }
      } catch (error) {
        console.error('Fehler beim Laden der zugeordneten Vorgänge:', error);
      }
    },
    alleVorgaengeVerlinken() {
      const neueVorgangIds = this.zugeordneteVorgaenge.filter(
        id =>
          !this.verlinkungen.some(verlinkung => verlinkung.typ === 'Vorgang' && verlinkung.artifactId == id)
      );

      if (neueVorgangIds.length === 0) {
        this.$bvToast.toast('Alle zugeordneten Vorgänge sind bereits verlinkt.', {
          title: 'Hinweis',
          variant: 'info',
          solid: true,
        });
        this.banner = null;
        this.zugeordneteVorgaenge = [];
        return;
      }

      const maxId = this.verlinkungen.length > 0 ? Math.max(...this.verlinkungen.map(v => v.id)) : -1;
      const neueVerlinkungen = neueVorgangIds.map((vorgangId, index) => ({
        id: maxId + 1 + index,
        typ: 'Vorgang',
        artifactId: vorgangId,
        artifactText: null,
      }));

      neueVerlinkungen.forEach(verlinkung => {
        this.$set(this.vorgangValidationStatuses, verlinkung.id, 'valid');
      });

      this.verlinkungen = [...this.verlinkungen, ...neueVerlinkungen];
      this.banner = null;
      this.zugeordneteVorgaenge = [];
    },
    changeArtifactReise(artifactId, verlinkungId) {
      const gewaehlt = this.reisekuerzelOptions.find(option => option.id === artifactId);
      if (!gewaehlt) {
        return;
      }
      const { id, reisekuerzel } = gewaehlt;

      const existingReise = this.verlinkungen.some(
        verlinkung => verlinkung.artifactId === id && verlinkung.typ === 'Reise'
      );
      if (existingReise) {
        this.$bvToast.toast('Diese Reise wurde bereits verlinkt.', {
          title: 'Warnung',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      this.verlinkungen = this.verlinkungen.map(verlinkung => {
        if (verlinkung.id === verlinkungId) {
          return { ...verlinkung, artifactId: id, artifactText: reisekuerzel };
        } else return verlinkung;
      });
    },
    changeArtifactFlugverfuegbarkeitOrVorgang(input, verlinkungsTyp, verlinkungsId) {
      if (verlinkungsTyp === 'Vorgang') {
        const existingVorgang = this.verlinkungen.some(
          verlinkung =>
            verlinkung.artifactId == input &&
            verlinkung.typ === verlinkungsTyp &&
            verlinkung.id !== verlinkungsId
        );

        if (existingVorgang) {
          this.verlinkungen.find(verlinkung => verlinkung.id === verlinkungsId).artifactId = '';
          this.clearVorgangValidationTimeout(verlinkungsId);
          this.$set(this.vorgangValidationStatuses, verlinkungsId, null);
          this.$bvToast.toast('Dieser Vorgang wurde bereits verlinkt.', {
            title: 'Warnung',
            variant: 'warning',
            solid: true,
          });
          return;
        }

        this.validateVorgangExists(verlinkungsId, input);
      }
    },
    clearVorgangValidationTimeout(verlinkungId) {
      if (this.vorgangValidationTimeouts[verlinkungId]) {
        clearTimeout(this.vorgangValidationTimeouts[verlinkungId]);
        this.vorgangValidationTimeouts[verlinkungId] = null;
      }
    },
    validateVorgangExists(verlinkungId, vorgangIdRaw) {
      this.clearVorgangValidationTimeout(verlinkungId);

      const trimmed = (vorgangIdRaw ?? '').toString().trim();

      // Vorgangsnummern sind 7-stellig - bei abweichender Länge keine Backend-Prüfung
      if (trimmed.length !== 7) {
        this.$set(this.vorgangValidationStatuses, verlinkungId, null);
        return;
      }

      const parsed = parseInt(trimmed, 10);
      if (Number.isNaN(parsed) || parsed <= 0) {
        this.$set(this.vorgangValidationStatuses, verlinkungId, null);
        return;
      }

      this.$set(this.vorgangValidationStatuses, verlinkungId, 'validating');

      this.vorgangValidationTimeouts[verlinkungId] = setTimeout(async () => {
        try {
          const response = await odataService.getVorgang({
            filter: { id: parsed },
            select: ['id'],
          });
          const currentVerlinkung = this.verlinkungen.find(v => v.id === verlinkungId);
          if (!currentVerlinkung) return;
          // Nur reagieren, wenn der Wert seit dem Aufruf nicht geändert wurde
          if (currentVerlinkung.artifactId?.toString().trim() !== trimmed) return;

          const exists = Array.isArray(response?.data) && response.data.length > 0;
          if (exists) {
            this.$set(this.vorgangValidationStatuses, verlinkungId, 'valid');
            // Aufrufer entscheidet über kontextspezifische Folgeaktionen (z.B. Reisetermine automatisch verlinken)
            this.$emit('vorgang-valid', trimmed);
          } else {
            currentVerlinkung.artifactId = '';
            this.$set(this.vorgangValidationStatuses, verlinkungId, null);
            this.$bvToast.toast(`Vorgang ${parsed} existiert nicht.`, {
              title: 'Warnung',
              variant: 'warning',
              solid: true,
            });
          }
        } catch (error) {
          this.$set(this.vorgangValidationStatuses, verlinkungId, 'invalid');
        }
      }, 500);
    },
    getVorgangInputState(verlinkung) {
      if (verlinkung.typ !== 'Vorgang') {
        return verlinkung.artifactId > 0;
      }
      const idStr = (verlinkung.artifactId ?? '').toString();
      if (idStr.length !== 7) {
        return false;
      }
      const status = this.vorgangValidationStatuses[verlinkung.id];
      if (status === 'valid') return true;
      if (status === 'invalid') return false;
      return null;
    },
    async getReisekuerzel() {
      let query = buildQuery({
        select: ['reisekuerzel', 'id'],
      });
      this.reisekuerzelOptions = await apiService
        .get('Reise' + query)
        .then(res => {
          return res?.data?.result?.value;
        })
        .catch(err => {
          console.err(err);
        });
    },
  },
};
</script>

<style scoped>
/* Hide number input spinners */
:deep(input[type='number'])::-webkit-outer-spin-button,
:deep(input[type='number'])::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.verlinkung-typ,
.verlinkung-artifact {
  --app-select-valid-border-color: var(--primary);
  --app-select-disabled-background: #e9ecef;
}

.vorgaenge-hinweis {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 38px;
  padding: 0 8px;
  margin-right: 22px;
  background-color: #eff6ff;
  border: 0.8px solid #dbeafe;
  border-radius: 10px;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: var(--text);
}

.vorgaenge-hinweis-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-right: 8px;
  border-radius: 6px;
  background-color: #dbeafe;
  color: var(--primary);
}

.vorgaenge-count {
  font-weight: 600;
  color: var(--primary);
}

.alle-verlinken-btn {
  width: 93px;
  height: 22px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface);
  border: 0.5px solid var(--primary);
  border-radius: 6px;
  box-shadow: 0px 0px 6px 0px #3699ff66;
  color: var(--primary);
  font-weight: 500;
  font-size: 11px;
  line-height: 14px;
}
</style>
